import React, {useState} from 'react'
import {SortableContainer, SortableElement, SortableHandle} from 'react-sortable-hoc'
import {removeNode, updateNode, selectNode} from '../../../editorActions'
import {ReactComponent as DragIcon} from '../../../../../assets/icons/drag.svg'
import {ReactComponent as LockIcon} from '../../../../../assets/icons/lock.svg'
import {ReactComponent as LockOpenIcon} from '../../../../../assets/icons/lock_open.svg'
import {ReactComponent as TrashIcon} from '../../../../../assets/icons/trash-alt.svg'
import {ReactComponent as TextThumbIcon} from '../../../../../assets/icons/text-thumb.svg'
import {ReactComponent as ImageThumbIcon} from '../../../../../assets/icons/image-thumb.svg'
import NodePropertiesToolbox from './NodePropertiesToolbox'

const DragHandle = SortableHandle(({locked}) => (
    <span
        className={`SidebarPanel__LayerAction ${locked ? 'SidebarPanel__LayerAction--disabled' : ''}`}
        aria-label="Reorder"
    >
        <DragIcon width={16} height={16} />
    </span>
))

function NodeListItemContent({node, isSelected, onSelect, onRemove, onLockToggle, useDragHandle}) {
    const isText = node.type === 'text'
    const title = isText ? node.content?.text || node.style?.text || 'Text' : 'Image'
    const subtitle = isText ? 'Text layer' : 'DPI 72'
    const locked = !!node.state?.locked
    const [imageError, setImageError] = useState(false)
    const imageSrc = node.content?.src ?? node.style?.src
    const showImagePreview = !isText && imageSrc && !imageError

    const handleRowClick = () => onSelect(node.id)

    const handleRemove = (e) => {
        e.stopPropagation()
        onRemove(node.id)
    }

    const handleLock = (e) => {
        e.stopPropagation()
        onLockToggle(node.id, !locked)
    }

    return (
        <div
            role="button"
            tabIndex={0}
            className={`SidebarPanel__LayerItem ${isSelected ? 'is-selected' : ''} ${!isText ? 'SidebarPanel__LayerItem--image' : ''}`}
            onClick={handleRowClick}
            onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault()
                    handleRowClick()
                }
            }}
        >
            <div className="SidebarPanel__LayerThumbnail">
                {isText ? (
                    <TextThumbIcon width={24} height={24} />
                ) : showImagePreview ? (
                    <img
                        src={imageSrc}
                        alt=""
                        onError={() => setImageError(true)}
                        style={{width: '100%', height: '100%', objectFit: 'cover', borderRadius: 'inherit'}}
                    />
                ) : (
                    <ImageThumbIcon width={24} height={24} />
                )}
            </div>
            <div className="SidebarPanel__LayerMeta">
                <div className="SidebarPanel__LayerName">{title}</div>
                <div className={`SidebarPanel__LayerSubtitle ${!isText ? 'SidebarPanel__LayerSubtitle--image' : ''}`}>
                    {subtitle}
                </div>
            </div>
            <div className="SidebarPanel__LayerActions">
                {useDragHandle ? (
                    <DragHandle locked={locked} />
                ) : (
                    <span
                        className="SidebarPanel__LayerAction SidebarPanel__LayerAction--disabled"
                        aria-label="Reorder"
                    >
                        <DragIcon width={16} height={16} />
                    </span>
                )}
                <button
                    type="button"
                    className="SidebarPanel__LayerAction"
                    onClick={handleLock}
                    aria-label={locked ? 'Unlock' : 'Lock'}
                >
                    {locked ? <LockIcon width={16} height={16} /> : <LockOpenIcon width={16} height={16} />}
                </button>
                <button type="button" className="SidebarPanel__LayerAction" onClick={handleRemove} aria-label="Delete">
                    <TrashIcon width={16} height={16} />
                </button>
            </div>
        </div>
    )
}

const SortableNodeItem = SortableElement(
    ({node, isSelected, onSelect, onRemove, onLockToggle, dispatch, onRemoveBackground}) => (
        <div className={`SidebarPanel__LayerRow ${isSelected ? 'is-selected' : ''}`}>
            <NodeListItemContent
                node={node}
                isSelected={isSelected}
                onSelect={onSelect}
                onRemove={onRemove}
                onLockToggle={onLockToggle}
                useDragHandle
            />
            {isSelected && (
                <NodePropertiesToolbox node={node} dispatch={dispatch} inline onRemoveBackground={onRemoveBackground} />
            )}
        </div>
    ),
)

const SortableList = SortableContainer(
    ({nodes, nodeIds, onSelect, onRemove, onLockToggle, dispatch, onRemoveBackground}) => (
        <div className="SidebarPanel__LayersList">
            {nodes.map((node, index) => (
                <SortableNodeItem
                    key={node.id}
                    index={index}
                    node={node}
                    isSelected={nodeIds.includes(node.id)}
                    onSelect={onSelect}
                    onRemove={onRemove}
                    onLockToggle={onLockToggle}
                    dispatch={dispatch}
                    onRemoveBackground={onRemoveBackground}
                    disabled={!!node.state?.locked}
                />
            ))}
        </div>
    ),
)

function NodesList({nodes, selection, selectedNode, dispatch, onReorder, onRemoveBackground}) {
    const nodeIds = selection?.nodeIds || []

    const handleRemove = (nodeId) => {
        dispatch(removeNode(nodeId))
    }

    const handleLockToggle = (nodeId, locked) => {
        dispatch(updateNode(nodeId, {state: {locked}}))
    }

    const handleSelect = (nodeId) => {
        dispatch(selectNode(nodeId, false))
    }

    const handleSortEnd = ({oldIndex, newIndex}) => {
        if (oldIndex === newIndex) return
        const nodeId = nodes[oldIndex]?.id
        if (nodeId) onReorder(nodeId, newIndex)
    }

    if (nodes.length === 0) {
        return (
            <div className="SidebarPanel__LayersList">
                <div className="SidebarPanel__Empty">No layers yet</div>
            </div>
        )
    }

    return (
        <SortableList
            nodes={nodes}
            nodeIds={nodeIds}
            onSelect={handleSelect}
            onRemove={handleRemove}
            onLockToggle={handleLockToggle}
            dispatch={dispatch}
            onRemoveBackground={onRemoveBackground}
            onSortEnd={handleSortEnd}
            useDragHandle
            lockAxis="y"
            helperClass="SidebarPanel__LayerItem--dragging"
        />
    )
}

export {NodesList, NodeListItemContent as NodeListItem}
